import { makeAutoObservable, runInAction } from 'mobx';
import { enableStaticRendering } from 'mobx-react-lite';
import { Session } from 'next-auth';
import {
    addTabRelatedChatSessionId,
    removeTabRelatedChatSessionId,
    startWSConnection,
    subscribeChat
} from '../../clients/chat/connection';
import { UI_STATES } from '../../components/plugins/CallApp';
import { CHAT_APP_UI_STATES, ChatEntity } from '../../components/plugins/chat/types';
import { PluginDefinition, PluginType } from '../../components/plugins/types';
import { AgentStatus, ChatTicketTab, agentStatusIdType } from '../../model/types';
import { getAgentCurrentStatus } from '../../services/agentStatus/agentStatusServices';
import { getPlugins } from '../../services/apps/plugins';
import { getTenantId } from '../../utils/accountHelper';
import { isSSR, pipe } from '../../utils/helpers';
import { getSecurityStore, getTabStore } from '../../utils/store/getStore';

enableStaticRendering(isSSR());

/**
 * 'manifest' and 'settings' are stored as string in the db, we need to parse them into javascript objects
 * @param pd
 */
function parsePluginDefinition(pd): PluginDefinition {
    try {
        if (pd.manifest) pd.manifest = JSON.parse(pd.manifest);
        if (pd.settings) pd.settings = JSON.parse(pd.settings);

        return pd;
    } catch (e) {
        return pd;
    }
}

const createPluginDefinitionObjectEntries = (pluginDefinition: PluginDefinition) => [
    pluginDefinition?.pluginId,
    parsePluginDefinition(pluginDefinition)
];

const isPluginType = (type: keyof typeof PluginType) => (plugin: PluginDefinition) => plugin?.pluginType === type;
const isChatPlugin = isPluginType('CHAT');
const isCallPlugin = isPluginType('CALL');
const isPagePlugin = isPluginType('PAGE');
const isRightPanelPlugin = isPluginType('RIGHT_PANEL');
const isTranslatePlugin = (plugin: PluginDefinition) => plugin?.pluginId === 'com.grispi.translate';

export type PluginErrorType = typeof UI_STATES.initializingError | null; // null means no error
export type PluginError = {
    // @PluginType can be extended (i.e. chat, etc.)
    [key in PluginType]: {
        [key: string]: PluginErrorType; // @PluginErrorType can be extended (i.e. connectingError, clientError, etc.)
    };
};

export type PluginIsInitializing = {
    // @PluginType can be extended (i.e. chat, etc.)
    [key in PluginType]: {
        [key: string]: boolean; // @PluginErrorType can be extended (i.e. connectingError, clientError, etc.)
    };
};

const initialObjectForPluginIdsGroupedByPluginType = pipe(
    Object.keys,
    (keys: PluginType[]) => keys.map((k) => [k, {}]),
    Object.fromEntries
)(PluginType);

export class PluginStore {
    pluginDefinitions: Record<string, PluginDefinition> | null; // pluginId:PluginDefinition
    callPlugin: PluginDefinition | null;
    chatPlugin: PluginDefinition | null;
    pagePlugins: Record<string, PluginDefinition>;
    private rightPanelPlugins: PluginDefinition[];
    translatePlugin: PluginDefinition | null;
    currentAgentStatus: AgentStatus | null;
    newAgentStatus: AgentStatus | null;
    changingAgentStatusIdTo: agentStatusIdType | null;
    pluginsErrors: PluginError;
    pluginIsInitializing: PluginIsInitializing;
    isAnyPluginInitializing: boolean;
    anyPluginInitializingCount: number;
    activeChatEntities: ChatEntity[];
    chatQueueCount: number;
    translateLanguageOptions: { value: string; label: string }[];

    constructor() {
        this.pluginDefinitions = null;
        this.callPlugin = null;
        this.chatPlugin = null;
        this.translatePlugin = null;
        this.pagePlugins = {};
        this.rightPanelPlugins = [];
        this.translatePlugin = null;
        this.currentAgentStatus = null;
        this.newAgentStatus = null;
        this.changingAgentStatusIdTo = null;
        this.pluginsErrors = initialObjectForPluginIdsGroupedByPluginType;
        this.pluginIsInitializing = initialObjectForPluginIdsGroupedByPluginType;
        this.isAnyPluginInitializing = false;
        this.anyPluginInitializingCount = 0;
        this.activeChatEntities = [];
        this.chatQueueCount = 0;
        this.translateLanguageOptions = [];

        // Call it here
        makeAutoObservable(this);
    }

    // https://mobx.js.org/actions.html#asynchronous-actions
    loadPluginDefinitions = async (session: Session): Promise<Record<string, PluginDefinition>> => {
        if (this.pluginDefinitions) {
            return this.pluginDefinitions;
        }

        try {
            // TODO: performance optimization, try to find a way to combine this request with useAgentStatus hook
            const currentAgentStatus = await getAgentCurrentStatus(session.userInfo.username);

            if (currentAgentStatus) {
                try {
                    const pluginDefinitionList = await getPlugins();

                    runInAction(() => {
                        this.setCurrentAgentStatusOnStore(currentAgentStatus?.status);
                        this.setPluginDefinitions(pluginDefinitionList);
                    });
                } catch (error) {
                    runInAction(() => {
                        this.setCurrentAgentStatusOnStore(currentAgentStatus?.status);
                    });
                    throw new Error(error);
                }

                return this.pluginDefinitions;
            }
        } catch (error) {
            throw new Error(error);
        }
    };

    // https://stackoverflow.com/questions/64770762/mobx-since-strict-mode-is-enabled-changing-observed-observable-values-withou
    setPluginDefinitions = (pluginDefinitionList: PluginDefinition[]): void => {
        const pluginDefinitionsEntries = pluginDefinitionList.map(createPluginDefinitionObjectEntries);
        const pluginDefinitionsObject = Object.fromEntries(pluginDefinitionsEntries);

        this.pluginDefinitions = pluginDefinitionsObject;

        this.callPlugin = pluginDefinitionList.find(isCallPlugin)?.enabled
            ? pluginDefinitionList.find(isCallPlugin)
            : null;
        this.chatPlugin = pluginDefinitionList.find(isChatPlugin)?.enabled
            ? pluginDefinitionList.find(isChatPlugin)
            : null;
        if (pluginDefinitionList.find(isChatPlugin)?.enabled) {
            startWSConnection(this.chatPlugin?.manifest?.src);
        }
        this.translatePlugin = pluginDefinitionList.find(isTranslatePlugin)?.enabled
            ? pluginDefinitionList.find(isTranslatePlugin)
            : null;

        const pagePluginsMap = pluginDefinitionList.filter(isPagePlugin).reduce((pagePluginsMap, plugin) => {
            pagePluginsMap[plugin.pluginId] = plugin;
            return pagePluginsMap;
        }, this.pagePlugins);
        this.pagePlugins = pagePluginsMap;

        const rightPanelPlugins = pluginDefinitionList.filter(isRightPanelPlugin);
        this.rightPanelPlugins = rightPanelPlugins;
    };

    resetPluginDefinitions = (): void => {
        this.pluginDefinitions = null;
    };

    resetChangingAgentStatusProcess = (): void => {
        this.changingAgentStatusIdTo = null;
        this.newAgentStatus = null;
    };

    setNewAgentStatus = (agentStatus: AgentStatus): void => {
        this.newAgentStatus = agentStatus;
    };

    setCurrentAgentStatusOnStore = (agentStatus: AgentStatus): void => {
        this.currentAgentStatus = agentStatus;
        // if (agentStatus.chat === 'ONLINE') subscribeActiveChatEntities();
        // if (agentStatus.chat !== 'ONLINE') unSubscribeActiveChatEntities();
    };

    changeAgentStatusOverMobXStore = (
        newAgentStatusId: agentStatusIdType
        // newCallPluginStatus: callPluginStatusType,
    ): void => {
        if (this.currentAgentStatus?.id === newAgentStatusId) {
            return;
        }

        this.changingAgentStatusIdTo = newAgentStatusId;
    };

    setPluginError = (type: PluginType, pluginId: string, error: typeof UI_STATES.initializingError): void => {
        this.pluginsErrors[type][pluginId] = error;
    };
    // below methods have to be extended as per @PluginType type
    setCallPluginError = (pluginId: string, error: typeof UI_STATES.initializingError): void => {
        this.setPluginError(PluginType.CALL, pluginId, error);
        this.setPluginIsInitializing(pluginId, PluginType.CALL, false);
    };
    setChatPluginError = (pluginId: string, error: typeof CHAT_APP_UI_STATES.initializingError): void => {
        this.setPluginError(PluginType.CHAT, pluginId, error);
        this.setPluginIsInitializing(pluginId, PluginType.CHAT, false);
    };
    setPagePluginError = (pluginId: string, error: typeof UI_STATES.initializingError): void => {
        this.setPluginError(PluginType.PAGE, pluginId, error);
        this.setPluginIsInitializing(pluginId, PluginType.PAGE, false);
    };
    setTranslatePluginError = (pluginId: string, error: typeof UI_STATES.initializingError): void => {
        this.setPluginError(PluginType.TRANSLATE, pluginId, error);
        this.setPluginIsInitializing(pluginId, PluginType.TRANSLATE, false);
    };

    setIsAnyPluginInitializing = (isAnyPluginInitializing: boolean): void => {
        if (isAnyPluginInitializing) {
            this.anyPluginInitializingCount = this.anyPluginInitializingCount + 1;
            this.isAnyPluginInitializing = isAnyPluginInitializing;
        } else {
            this.anyPluginInitializingCount = this.anyPluginInitializingCount - 1;

            if (this.anyPluginInitializingCount < 1) {
                this.isAnyPluginInitializing = isAnyPluginInitializing;
                this.anyPluginInitializingCount = 0;
            }
        }
    };
    setPluginIsInitializing = (pluginId: string, pluginType: PluginType, isInitializing: boolean): void => {
        this.pluginIsInitializing[pluginType][pluginId] = isInitializing;
        this.setIsAnyPluginInitializing(isInitializing);
    };

    addToActiveChatEntities = (chatEntity: ChatEntity): void => {
        this.setActiveChatEntities([...this.activeChatEntities, chatEntity]);
    };
    setActiveChatEntities = (chatEntities: ChatEntity[]): void => {
        (getTabStore().tabs ?? [])
            .filter((tab) => tab.type === 'TICKET' && tab.channel === 'CHAT')
            .map(({ key: tabKey, chatSessionId }: ChatTicketTab) => {
                const relatedChatEntity = chatEntities.find((chatEntity) => chatEntity.ticketKey === tabKey);

                if (relatedChatEntity) {
                    getTabStore().injectChatInfoToChatTicketTab(relatedChatEntity);
                    addTabRelatedChatSessionId(relatedChatEntity.chatSessionId);
                } else {
                    getTabStore().injectChatInfoToChatTicketTab({
                        ticketKey: tabKey,
                        chatId: null,
                        chatSessionId: null
                    });
                    removeTabRelatedChatSessionId(chatSessionId);
                }
            });
    };
    subscribeActiveChatEntities = (chatEntities: ChatEntity[]) => {
        this.activeChatEntities = chatEntities;
        chatEntities.forEach((chatEntity) => {
            subscribeChat({
                brokerUrl: this.chatPlugin?.manifest?.src,
                chatSessionId: chatEntity.chatSessionId,
                tenantId: getTenantId(),
                token: getSecurityStore()?.accessToken
            });
        });
    };

    closeChatSession = (chatSessionId: string): void => {
        const filteredChatEntities = this.activeChatEntities.filter((entity) => entity.chatSessionId !== chatSessionId);
        this.setActiveChatEntities(filteredChatEntities);
    };

    getRightPanelPlugins = () => {
        return this.rightPanelPlugins;
    };

    getTranslateLanguageOptions = () => {
        return this.translateLanguageOptions;
    };
    setTranslateLanguageOptions = (languages) => {
        this.translateLanguageOptions = languages;
    };
}
